/**
 * Tool Result Renderer Registry
 *
 * Central registry mapping tool types to their renderer components.
 * This file is the single point of modification when adding new tool renderers.
 *
 * To add a new tool renderer:
 * 1. Create the renderer component (e.g., StripePaymentRenderer.tsx)
 * 2. Import it below
 * 3. Add one line to the TOOL_RENDERERS map
 *
 * That's it! The system will automatically route to your renderer.
 */

import React from 'react';
import type { ToolResult, ToolResultRendererProps } from '../../../types/toolResults';

// Import all renderer components
import DomainAvailabilityRenderer from './DomainAvailabilityRenderer';
import DomainSearchRenderer from './DomainSearchRenderer';
import DomainPricingRenderer from './DomainPricingRenderer';
import DomainRDAPAvailabilityRenderer from './DomainRDAPAvailabilityRenderer';
import DefaultTextRenderer from './DefaultTextRenderer';

/**
 * Type for renderer component.
 * All renderers must accept ToolResultRendererProps.
 */
type RendererComponent = React.FC<ToolResultRendererProps<any>>;

/**
 * Tool renderer registry map.
 *
 * Maps tool types (from ToolResult.toolType) to their renderer components.
 *
 * ADD NEW RENDERERS HERE:
 * 'tool_type_name': RendererComponent
 */
export const TOOL_RENDERERS: Record<string, RendererComponent> = {
  // GoDaddy tools
  'godaddy_check_availability': DomainAvailabilityRenderer,
  'godaddy_search_domains': DomainSearchRenderer,
  'godaddy_get_pricing': DomainPricingRenderer,

  // AC-1444 (epic AC-1434): RDAP-based domain availability tool. Used both
  // by the agent tool surface (`rdap_check_availability` AgentTool) and by
  // the `rdap_lookup` orchestration step's RunResult viewer -- both surfaces
  // emit the same `RDAPLookupResponse` shape, so a single renderer covers
  // them. `parseToolResult` (in types/toolResults.ts) routes both shapes here.
  'rdap_check_availability': DomainRDAPAvailabilityRenderer,

  // Future tool examples (commented out until implemented):
  // 'stripe_create_payment': StripePaymentRenderer,
  // 'sendgrid_send_email': SendGridEmailRenderer,
  // 'twilio_send_sms': TwilioSMSRenderer,
  // 'github_create_issue': GitHubIssueRenderer,
  // 'slack_send_message': SlackMessageRenderer,
};

/**
 * Get the appropriate renderer component for a tool result.
 *
 * @param toolType - The tool type from ToolResult
 * @returns Renderer component or DefaultTextRenderer if not found
 */
export function getRendererForToolType(toolType: string): RendererComponent {
  return TOOL_RENDERERS[toolType] || DefaultTextRenderer;
}

/**
 * Check if a custom renderer exists for a tool type.
 *
 * @param toolType - The tool type to check
 * @returns True if custom renderer exists, false if will use default
 */
export function hasCustomRenderer(toolType: string): boolean {
  return toolType in TOOL_RENDERERS;
}

/**
 * Get list of all registered tool types with custom renderers.
 *
 * @returns Array of tool type strings
 */
export function getRegisteredToolTypes(): string[] {
  return Object.keys(TOOL_RENDERERS);
}

/**
 * Render a tool result with the appropriate renderer component.
 *
 * @param result - Tool result to render
 * @param props - Additional props to pass to renderer
 * @returns Rendered component
 */
export function renderToolResult(
  result: ToolResult,
  props?: Partial<ToolResultRendererProps>
): React.ReactElement {
  const Renderer = getRendererForToolType(result.toolType);
  return React.createElement(Renderer, {
    result,
    ...props
  });
}

// Export all renderers for direct import if needed
export {
  DomainAvailabilityRenderer,
  DomainSearchRenderer,
  DomainPricingRenderer,
  DomainRDAPAvailabilityRenderer,
  DefaultTextRenderer
};
